import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type RefObject,
} from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import {
  apiErrorStatus,
  apiErrorText,
  apiLoadErrorText,
  useArchiveFleetModelMutation,
  useGetFleetModelQuery,
  useListFleetCategoriesQuery,
  useRestoreFleetModelMutation,
  type FleetModel,
  type FleetModelDetail,
} from '@shared/api';
import { useActiveProjectContext } from '@shared/lib/activeProjectContext';
import { pluralizeRu } from '@shared/lib/formatFile';
import { Button } from '@shared/ui/Button';
import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerFooter,
  DrawerHeader,
  DrawerIconButton,
} from '@shared/ui/Drawer';
import { SkeletonLine } from '@shared/ui/Skeleton';
import { IconChevronRight, IconPencil } from '@shared/ui/icons';
import {
  FleetStatusChip,
  ModelPrcBlock,
  PassportView,
  PrcStaleChip,
  RUB_NO_VAT,
  SpecGrid,
  fleetLeading,
  fleetType,
  formatCount,
  formatDateTime,
  formatMoney,
  modelSourceLabel,
  todayIso,
  typo,
} from '@entities/fleet';
import { JournalPanel } from './JournalPanel';
import { ModelForm } from './ModelForm';
import { UnitsPanel } from './UnitsPanel';
import { Actions, FooterStatus, Notice, TabPanel, Tabs, TextButton, type TabItem } from './ui';

/** Значение `?model=` для создания новой модели. */
export const NEW_MODEL = 'new';

type CardTab = 'passport' | 'units' | 'journal';
type Mode = 'view' | 'edit';

const HeaderTop = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  min-height: 36px;
`;

const Eyebrow = styled.p`
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
  font-size: ${fleetType.micro};
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.text.secondary};
  overflow-wrap: anywhere;
`;

const Position = styled.span`
  font-size: ${fleetType.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

const PrevIcon = styled(IconChevronRight)`
  transform: rotate(180deg);
`;

const Title = styled.h2`
  margin: ${({ theme }) => `${theme.spacing(1)} 0 0`};
  font-size: ${fleetType.title};
  font-weight: 600;
  line-height: ${fleetLeading.tight};
  letter-spacing: -0.01em;
  text-wrap: balance;
  overflow-wrap: anywhere;

  /* Заголовок получает фокус при открытии, чтобы скринридер прочёл имя
     модели. Это не элемент управления — рамка фокуса ему не нужна. */
  &:focus,
  &:focus-visible {
    outline: none;
    box-shadow: none;
  }
`;

const MetaRow = styled.div<{ $last: boolean }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(2)}`};
  margin-top: ${({ theme }) => theme.spacing(2.5)};
  padding-bottom: ${({ theme, $last }) => ($last ? theme.spacing(4) : 0)};

  & > .text {
    font-size: ${fleetType.small};
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

const Comment = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing(4)};
  font-size: ${fleetType.body};
  line-height: ${fleetLeading.normal};
  white-space: pre-wrap;

  & > .k {
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

const Stack = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

/** Ссылка-действие в тексте: «Открыть версии ПРЦ». */
const InlineLink = styled(Link)`
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 600;
  text-underline-offset: 3px;

  &:hover {
    text-decoration: underline;
  }
`;

function SpecStrip({ model }: { model: FleetModel }) {
  const cell = (value: string | null) => (value ? <dd>{value}</dd> : <dd className="empty">—</dd>);
  return (
    <SpecGrid aria-label="Ключевые данные модели">
      <div className="span2">
        <dt>Текущая стоимость, {RUB_NO_VAT}</dt>
        <dd className={model.current_cost ? 'big' : 'big empty'}>
          {formatMoney(model.current_cost)}
        </dd>
      </div>
      <div>
        <dt>Единиц</dt>
        <dd className="big">{formatCount(model.units_count)}</dd>
      </div>
      <div>
        <dt>Топливо</dt>
        {cell(model.fuel_kind)}
      </div>
      <div className="span2">
        <dt>Собственник</dt>
        {cell(model.owner_name)}
      </div>
      <div className="span2">
        <dt>Должность механизатора</dt>
        {cell(model.operator_position)}
      </div>
    </SpecGrid>
  );
}

function BodySkeleton() {
  return (
    <DrawerBody aria-busy="true">
      <SkeletonLine $height={64} />
      <SkeletonLine $width="60%" />
      <SkeletonLine $width="85%" />
      <SkeletonLine $width="70%" />
    </DrawerBody>
  );
}

interface ViewProps {
  detail: FleetModelDetail;
  canWrite: boolean;
  tab: CardTab;
  idPrefix: string;
  flash: string | null;
  onEdit: () => void;
}

/** Карточка в режиме просмотра: вкладки и действия. */
function ModelView({ detail, canWrite, tab, idPrefix, flash, onEdit }: ViewProps) {
  const { model, units } = detail;
  const archived = model.status === 'archived';
  // ПРЦ в карточке — объекта из шапки ОСНОВЫ (своя или общая).
  const { activeProjectId } = useActiveProjectContext();
  const [confirming, setConfirming] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [archive, { isLoading: archiving }] = useArchiveFleetModelMutation();
  const [restore, { isLoading: restoring }] = useRestoreFleetModelMutation();
  const archiveRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (confirming) confirmRef.current?.focus();
  }, [confirming]);

  async function doArchive() {
    setActionError(null);
    try {
      await archive(model.id).unwrap();
      setConfirming(false);
    } catch (err) {
      setActionError(apiErrorText(err, 'Не удалось отправить модель в архив.'));
    }
  }

  async function doRestore() {
    setActionError(null);
    try {
      await restore(model.id).unwrap();
    } catch (err) {
      setActionError(
        apiErrorStatus(err) === 409
          ? apiErrorText(err, 'Вернуть нельзя: модель с таким наименованием уже в справочнике.')
          : apiErrorText(err, 'Не удалось вернуть модель из архива.'),
      );
    }
  }

  function cancelConfirm() {
    setConfirming(false);
    requestAnimationFrame(() => archiveRef.current?.focus());
  }

  return (
    <>
      <DrawerBody>
        {tab === 'passport' && (
          <TabPanel idPrefix={idPrefix} id="passport">
            <SpecStrip model={model} />
            {(model.prc_stale || archived) && (
              <Stack>
                {model.prc_stale && (
                  <Notice $tone="warning">
                    <span className="text">
                      <strong>ПРЦ устарела.</strong>{' '}
                      {typo(
                        'Исходные данные расчёта изменились после утверждения цены машино-часа — модель попадёт в список на пересчёт.',
                      )}
                    </span>
                  </Notice>
                )}
                {archived && (
                  <Notice $tone="info">
                    <span className="text">
                      {typo(
                        'Модель в архиве: в расчётах и списках по умолчанию её нет. Удаления в справочнике нет — модель можно вернуть.',
                      )}
                    </span>
                  </Notice>
                )}
              </Stack>
            )}
            <ModelPrcBlock
              history={detail.prc_history ?? []}
              projectId={activeProjectId || undefined}
              today={todayIso()}
              idPrefix={idPrefix}
              emptyAction={
                canWrite ? (
                  <InlineLink to="/agents/econ/prc">Открыть версии ПРЦ</InlineLink>
                ) : undefined
              }
            />
            {model.comment && (
              <Comment>
                <span className="k">Комментарий: </span>
                {model.comment}
              </Comment>
            )}
            <PassportView passport={model.passport} idPrefix={`${idPrefix}-p`} />
          </TabPanel>
        )}
        {tab === 'units' && (
          <TabPanel idPrefix={idPrefix} id="units">
            <UnitsPanel model={model} units={units} canWrite={canWrite} />
          </TabPanel>
        )}
        {tab === 'journal' && (
          <TabPanel idPrefix={idPrefix} id="journal">
            <JournalPanel modelId={model.id} />
          </TabPanel>
        )}
      </DrawerBody>
      {canWrite && (
        <DrawerFooter>
          {confirming ? (
            <ConfirmArchive
              busy={archiving}
              confirmRef={confirmRef}
              onConfirm={() => void doArchive()}
              onCancel={cancelConfirm}
            />
          ) : (
            <>
              <FooterStatus role="status">{actionError ?? flash ?? ''}</FooterStatus>
              <Actions>
                {archived ? (
                  <Button
                    type="button"
                    $variant="secondary"
                    disabled={restoring}
                    onClick={() => void doRestore()}
                  >
                    {restoring ? 'Возвращаю…' : 'Вернуть из архива'}
                  </Button>
                ) : (
                  <>
                    <Button
                      ref={archiveRef}
                      type="button"
                      $variant="ghost"
                      onClick={() => {
                        setActionError(null);
                        setConfirming(true);
                      }}
                    >
                      В архив
                    </Button>
                    <Button type="button" $variant="primary" onClick={onEdit}>
                      <IconPencil size={16} />
                      Изменить
                    </Button>
                  </>
                )}
              </Actions>
            </>
          )}
        </DrawerFooter>
      )}
    </>
  );
}

function ConfirmArchive({
  busy,
  confirmRef,
  onConfirm,
  onCancel,
}: {
  busy: boolean;
  confirmRef: RefObject<HTMLButtonElement | null>;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onCancel();
    }
  }
  return (
    <Actions role="group" aria-label="Подтверждение архивации" onKeyDown={onKeyDown}>
      <FooterStatus>
        {typo('Отправить модель в архив? Из списка она пропадёт, вернуть её можно в любой момент.')}
      </FooterStatus>
      <Button ref={confirmRef} type="button" $variant="danger" disabled={busy} onClick={onConfirm}>
        {busy ? 'Отправляю…' : 'В архив'}
      </Button>
      <Button type="button" $variant="ghost" disabled={busy} onClick={onCancel}>
        Отмена
      </Button>
    </Actions>
  );
}

interface ModelDrawerProps {
  /** id модели или `NEW_MODEL`. */
  modelId: string;
  /** Строка списка — шапка видна сразу, пока грузится паспорт. */
  preview: FleetModel | undefined;
  canWrite: boolean;
  /** id моделей в порядке списка — для «предыдущая / следующая». */
  siblings: readonly string[];
  onSwitch: (id: string) => void;
  onClose: () => void;
  onCreated: (id: string) => void;
}

/**
 * Карточка модели в боковой панели: паспорт по блокам, единицы, журнал
 * правок; правка — в той же панели. Шапка с «предыдущая / следующая» не
 * пересоздаётся при переходе — фокус остаётся на кнопке.
 */
export function ModelDrawer({
  modelId,
  preview,
  canWrite,
  siblings,
  onSwitch,
  onClose,
  onCreated,
}: ModelDrawerProps) {
  const isNew = modelId === NEW_MODEL;
  const uid = useId();
  const titleId = `${uid}-title`;
  const formId = `${uid}-form`;
  const titleRef = useRef<HTMLHeadingElement>(null);
  const dirtyRef = useRef(0);

  const { data: detail, error } = useGetFleetModelQuery(modelId, { skip: isNew });
  const { data: categories } = useListFleetCategoriesQuery();

  // Состояние карточки сбрасывается при переходе к другой модели.
  const [ui, setUi] = useState<{
    id: string;
    mode: Mode;
    tab: CardTab;
    session: number;
    flash: string | null;
  }>(() => ({
    id: modelId,
    mode: isNew ? 'edit' : 'view',
    tab: 'passport',
    session: 0,
    flash: null,
  }));
  if (ui.id !== modelId) {
    setUi({
      id: modelId,
      mode: isNew ? 'edit' : 'view',
      tab: 'passport',
      session: ui.session + 1,
      // Только что созданная модель открывается с сообщением «добавлена».
      flash: ui.id === NEW_MODEL ? ui.flash : null,
    });
  }

  const current = detail && detail.model.id === modelId ? detail : undefined;
  const model = current?.model ?? preview;
  const editing = ui.mode === 'edit';

  const index = siblings.indexOf(modelId);
  const prev = index > 0 ? siblings[index - 1] : undefined;
  const next = index >= 0 && index < siblings.length - 1 ? siblings[index + 1] : undefined;

  const onDirtyChange = useCallback((count: number) => {
    dirtyRef.current = count;
  }, []);

  const confirmDiscard = useCallback(() => {
    const n = dirtyRef.current;
    return (
      n === 0 ||
      window.confirm(
        `Закрыть без сохранения? Правки в ${n} ${pluralizeRu(n, ['поле', 'полях', 'полях'])} пропадут.`,
      )
    );
  }, []);

  const requestClose = useCallback(() => {
    if (!confirmDiscard()) return;
    dirtyRef.current = 0;
    onClose();
  }, [confirmDiscard, onClose]);

  const focusTitle = () =>
    requestAnimationFrame(() => titleRef.current?.focus({ preventScroll: true }));

  function startEdit() {
    setUi((s) => ({ ...s, mode: 'edit', session: s.session + 1, flash: null }));
  }

  function cancelEdit() {
    if (!confirmDiscard()) return;
    dirtyRef.current = 0;
    if (isNew) {
      onClose();
      return;
    }
    setUi((s) => ({ ...s, mode: 'view' }));
    focusTitle();
  }

  function saved(result: FleetModel) {
    dirtyRef.current = 0;
    if (isNew) {
      setUi((s) => ({ ...s, flash: 'Модель добавлена' }));
      onCreated(result.id);
      focusTitle();
      return;
    }
    setUi((s) => ({ ...s, mode: 'view', flash: 'Изменения сохранены' }));
    focusTitle();
  }

  const tabs: TabItem<CardTab>[] = [
    { id: 'passport', label: 'Паспорт' },
    { id: 'units', label: 'Единицы', count: current ? current.units.length : undefined },
    { id: 'journal', label: 'Журнал правок' },
  ];

  const notFound = !isNew && error !== undefined && apiErrorStatus(error) === 404;

  return (
    <Drawer open onClose={requestClose} labelledBy={titleId} initialFocusRef={titleRef}>
      <DrawerHeader>
        <HeaderTop>
          <Eyebrow>
            {isNew
              ? 'Новая модель'
              : editing
                ? 'Правка модели'
                : (model?.category_name ?? 'Модель техники')}
          </Eyebrow>
          {!isNew && siblings.length > 1 && index >= 0 && (
            <>
              <Position aria-hidden="true">
                {index + 1} / {siblings.length}
              </Position>
              <DrawerIconButton
                type="button"
                aria-label="Предыдущая модель"
                title={editing ? 'Сначала сохраните или отмените правку' : 'Предыдущая модель'}
                disabled={!prev || editing}
                onClick={() => prev && onSwitch(prev)}
              >
                <PrevIcon size={18} />
              </DrawerIconButton>
              <DrawerIconButton
                type="button"
                aria-label="Следующая модель"
                title={editing ? 'Сначала сохраните или отмените правку' : 'Следующая модель'}
                disabled={!next || editing}
                onClick={() => next && onSwitch(next)}
              >
                <IconChevronRight size={18} />
              </DrawerIconButton>
            </>
          )}
          <DrawerCloseButton onClick={requestClose} />
        </HeaderTop>
        <Title id={titleId} ref={titleRef} tabIndex={-1}>
          {isNew ? 'Добавление модели техники' : (model?.name ?? 'Модель техники')}
        </Title>
        <MetaRow $last={editing || !model}>
          {model && !isNew && (
            <>
              <FleetStatusChip status={model.status} />
              {model.prc_stale && <PrcStaleChip />}
              <span className="text">
                {modelSourceLabel(model.source)} · изменена {formatDateTime(model.updated_at)}
              </span>
            </>
          )}
          {isNew && (
            <span className="text">
              {typo('Заполните наименование и то, что известно, — остальное можно дописать потом.')}
            </span>
          )}
        </MetaRow>
        {!editing && model && (
          <Tabs
            items={tabs}
            value={ui.tab}
            onChange={(tab) => setUi((s) => ({ ...s, tab }))}
            idPrefix={uid}
            label="Разделы карточки"
          />
        )}
      </DrawerHeader>

      {isNew ? (
        <ModelForm
          key={`new-${ui.session}`}
          model={null}
          formId={formId}
          categories={categories?.items ?? []}
          onCancel={cancelEdit}
          onSaved={saved}
          onDirtyChange={onDirtyChange}
        />
      ) : notFound ? (
        <DrawerBody>
          <Notice $tone="danger" role="alert">
            <span className="text">{typo('Модель не найдена — возможно, ссылка устарела.')}</span>
            <TextButton type="button" onClick={onClose}>
              К списку
            </TextButton>
          </Notice>
        </DrawerBody>
      ) : error !== undefined && !current ? (
        <DrawerBody>
          <Notice $tone="danger" role="alert">
            <span className="text">
              {apiLoadErrorText(
                error,
                'Не удалось открыть карточку модели.',
                'Нет доступа к справочнику техники.',
              )}
            </span>
          </Notice>
        </DrawerBody>
      ) : !current ? (
        <BodySkeleton />
      ) : editing ? (
        <ModelForm
          key={`${modelId}-${ui.session}`}
          model={current.model}
          formId={formId}
          categories={categories?.items ?? []}
          onCancel={cancelEdit}
          onSaved={saved}
          onDirtyChange={onDirtyChange}
        />
      ) : (
        <ModelView
          key={modelId}
          detail={current}
          canWrite={canWrite}
          tab={ui.tab}
          idPrefix={uid}
          flash={ui.flash}
          onEdit={startEdit}
        />
      )}
    </Drawer>
  );
}
